import { Trans, useLingui } from '@lingui/react/macro';
import * as React from 'react';
import Button from 'components/Button';
import StarRating from '..';
import { Field, Label, Textarea } from '@headlessui/react';
import { IPublicSession, userSession } from 'state/session';
import { sendMetric } from 'utils/network/postAnalytics';
import { IAnalyticsRatingData } from 'data/analytics';
import Icon from 'components/Icon';
import { user } from 'utils/auth/user';
import { classNames } from 'utils/ui/css';

type Rating = {
  starsCount: number;
  comment: string;
};

export interface IRatingModalProps {
  onModalClose?: () => void;
  xPosition?: 'justify-start' | 'justify-center' | 'justify-end';
  yPosition?: 'items-start' | 'items-center' | 'items-end';
  session?: IPublicSession;
}

const RatingModal: React.FC<IRatingModalProps> = ({
  onModalClose,
  xPosition = 'justify-center',
  yPosition = 'items-center',
  session = userSession().get(),
}) => {
  const { t } = useLingui();
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(true);
  const [extendedRating, setExtendedRating] = React.useState<Rating>({
    starsCount: 0,
    comment: '',
  });

  const handleCloseModal = React.useCallback(() => {
    setIsModalOpen(false);
    userSession().setValues({
      ratingCompleted: true,
    });
    onModalClose?.();
  }, [onModalClose]);

  const handleRatingSubmit = () => {
    handleCloseModal();

    sendMetric<IAnalyticsRatingData>('rating', {
      sessionId: session.id ?? 'unknown-session-id',
      mmToken: session.aveSessionId ?? '',
      rating: `${extendedRating.starsCount}`,
      comment: extendedRating.comment,
      user: user.type,
      timestamp: `${Date.now()}`,
    });
  };

  const handleStarCountSubmit = (starsNumber: number) => {
    setExtendedRating((prev) => ({ ...prev, starsCount: starsNumber }));
  };

  const handleCommentChange = (comment: string) => {
    setExtendedRating((prev) => ({ ...prev, comment }));
  };

  // Add an event listener to close the modal when the Escape key is pressed
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCloseModal]);

  const classes = classNames(
    'fixed z-50 flex overflow-y-auto overflow-x-hidden outline-none focus:outline-none',
    user.type === 'dealer' && `inset-0 m-0 items-center justify-center bg-black/50`,
    user.type === 'customer' && `bottom-0 sm:right-0 mx-6 my-14 ${yPosition} ${xPosition}`
  );

  return isModalOpen ? (
    <div className={classes} data-testid="star-rating-modal">
      <div className="relative my-6 w-auto max-w-[400px]">
        <div className="relative flex w-full flex-col rounded-lg border-0 bg-white text-black shadow-lg outline-none focus:outline-none">
          {/*body*/}
          <div className="relative w-[95vw] max-w-[400px] flex-auto items-center p-6">
            <div className="flex shrink-0 justify-end">
              <button
                type="button"
                onClick={handleCloseModal}
                className="inline-flex rounded-md text-gray-900 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-100 focus:ring-offset-2"
                data-testid="close-rating-modal"
              >
                <span className="sr-only">
                  <Trans>Close</Trans>
                </span>
                <Icon aria-hidden="true" className="ex3d-icon--close size-5 bg-teal" />
              </button>
            </div>
            {extendedRating.starsCount === 0 ? (
              <StarRating question={t`How was your session?`} onSubmit={handleStarCountSubmit} />
            ) : (
              <>
                <Field className="mt-3">
                  <Label>
                    {extendedRating.starsCount <= 4
                      ? t`How can we improve?`
                      : t`Do you have any comment or recommendation?`}
                  </Label>
                  <Textarea
                    data-testid="rating-comment"
                    className="bg-white/5 mt-3 block w-full resize-none rounded-lg border px-3 py-1.5 text-sm/6"
                    rows={3}
                    onChange={(e) => handleCommentChange(e.target.value)}
                  />
                </Field>
                <div className="flex items-center rounded-b pt-5">
                  <Button
                    className="mr-3 rounded-3xl bg-teal text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                    svgClassName="size-6 mx-2 bg-white"
                    text={t`Submit`}
                    onClick={handleRatingSubmit}
                    data-testid="submit-rating"
                  ></Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default RatingModal;
